/**
 * One target: its headline state, its window, its checks, and its queues.
 *
 * The header answers "should I care right now" without scrolling - status, HTTP code,
 * round trip, availability meter and the timeline strip. Everything below is the
 * evidence for it.
 *
 * Two notices earn their space in the header. `key_withheld` says the dashboard chose
 * not to send this site's API key because the host is not on its trusted list, which
 * is why the card is showing the thin public view - without it, a manual host would
 * silently look less healthy than the same service polled by its configured name. And
 * a `public` view badge says the detail is missing because of a credential, not because
 * the service had nothing to say.
 */

import {
  AlertTriangle,
  ExternalLink,
  KeyRound,
  Lock,
  Pencil,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  SquareArrowOutUpRight,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'

import type {
  ApiKeyCheck,
  ApiKeyCheckState,
  HistorySummary,
  LatencyThresholds,
  QueueSnapshot,
  SiteInfo,
  TargetResult,
} from '../api/types'
import { cn } from '../lib/cn'
import { formatMs, formatPct, formatRelative } from '../lib/format'
import {
  latencyBand,
  latencyFill,
  resolveThresholds,
  statusText,
  STATUS_PRESENTATION,
} from '../lib/status'
import { Meter } from './charts/Meter'
import { StatusTimeline } from './charts/StatusTimeline'
import { QueuePanel } from './QueuePanel'
import { RawPayload } from './RawPayload'
import { ServiceList } from './ServiceList'
import { IntervalControl } from './ui/IntervalControl'
import { ModeToggle } from './ui/ModeToggle'
import { StatusPill } from './ui/StatusPill'
import { Tooltip } from './ui/Tooltip'

interface TargetCardProps {
  result: TargetResult
  history: HistorySummary | undefined
  /** The dashboard-wide bands, used when `site` is unknown or names none of its own. */
  thresholds: LatencyThresholds
  /**
   * The site this target belongs to. Carries its latency bands - the site's own for the
   * round trip in the header, and per-check ones for the rows below.
   */
  site?: SiteInfo
  onProbe: (result: TargetResult) => void
  isProbing: boolean
  /** Samples already trimmed to the selected window. */
  samples: HistorySummary['samples']
  /** Only offered for manual targets - configured ones live in sites.json. */
  onDelete?: (result: TargetResult) => void
  isDeleting?: boolean
  /** The inline edit panel for a manual target, rendered when the pencil is toggled. */
  editSlot?: React.ReactNode
  onToggleEdit?: () => void
  isEditing?: boolean
  /**
   * The grip that starts a drag, from SortableGrid. Rendered ahead of the title so it
   * reads as "this card's handle" rather than as another action button on the right,
   * and omitted entirely where the card is not sortable (the detail page).
   */
  dragHandle?: React.ReactNode
  /** The dashboard-route href for this target's full-page detail view. */
  detailHref: string
  /** Open the detail view in this tab (the icon button opens a new one). */
  onOpenDetail: (result: TargetResult) => void
  /** Mode + interval controls. Omitted on the read-only detail page's embedded card. */
  controls?: TargetCardControls
}

export interface TargetCardControls {
  /** Why detailed mode is unavailable for this target, or null if it is available. */
  detailedDisabledReason: string | null
  onSetMode: (mode: 'generic' | 'detailed') => void
  modePending: boolean
  intervalSeconds: number
  intervalIsCustom: boolean
  onSetInterval: (seconds: number | null) => void
  intervalPending: boolean
  /** Verify the key this target already holds. Issues nothing either way. */
  onCheckKey: () => void
  checkKeyPending: boolean
  /** The last verdict for this card, or null if the button has not been pressed. */
  keyCheck: ApiKeyCheck | null
  /** Set when the check call itself failed (not the verify endpoint's verdict). */
  keyCheckError: string | null
}

/** How each verdict reads on the card: its icon, its tone, and its lead-in. */
const KEY_CHECK_PRESENTATION: Record<
  ApiKeyCheckState,
  { icon: typeof KeyRound; tone: string; label: string }
> = {
  valid: { icon: ShieldCheck, tone: 'text-good', label: 'Key valid' },
  invalid: { icon: ShieldAlert, tone: 'text-critical', label: 'Key rejected' },
  unverifiable: { icon: KeyRound, tone: 'text-ink-muted', label: 'Cannot be checked' },
  none: { icon: KeyRound, tone: 'text-ink-muted', label: 'No key held' },
  withheld: { icon: Lock, tone: 'text-warning', label: 'Key withheld' },
  error: { icon: AlertTriangle, tone: 'text-warning', label: 'Check failed' },
}

export function TargetCard({
  result,
  history,
  thresholds,
  site,
  onProbe,
  isProbing,
  samples,
  onDelete,
  isDeleting,
  editSlot,
  onToggleEdit,
  isEditing,
  dragHandle,
  detailHref,
  onOpenDetail,
  controls,
}: TargetCardProps) {
  const [showRaw, setShowRaw] = useState(false)
  // Two-step rather than a window.confirm: deleting discards the sample window, so it
  // deserves a beat, but a modal for one button is heavier than the action warrants.
  const [confirmDelete, setConfirmDelete] = useState(false)

  // The whole round trip, so the site's own bands - no service name to narrow by.
  const band = latencyBand(result.elapsed_ms, resolveThresholds(thresholds, site))
  const presentation = STATUS_PRESENTATION[result.status]

  // The queue snapshot lives on whichever check published it - `queues`, in practice.
  const queueSnapshot = result.services.find(
    (service): service is typeof service & { data: QueueSnapshot } =>
      Boolean(service.data?.families && service.data?.totals),
  )?.data as QueueSnapshot | undefined

  const issueEntries = Object.entries(result.issues)

  return (
    <article
      className="card overflow-hidden"
      style={{
        // A hairline of the status colour along the top edge, so a wall of cards can be
        // scanned at a glance. The pill below carries the icon and the word.
        borderTopWidth: 3,
        borderTopColor: `var(--color-${presentation.token})`,
      }}
    >
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 border-b p-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {dragHandle}
            {/*
              The title is the primary way into the detail view. A real anchor, so a
              plain click navigates in this tab and Ctrl/Cmd/middle-click opens a new one
              - the behaviours a link is expected to have, for free. The dedicated button
              on the right is the discoverable "new tab" affordance for a plain click.
            */}
            <a
              href={detailHref}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
                event.preventDefault()
                onOpenDetail(result)
              }}
              className="text-sm font-semibold text-ink hover:underline"
            >
              {result.site_name}
            </a>
            <span className="rounded-full border px-2 py-px text-[11px] text-ink-secondary">
              {result.env_label}
            </span>
            {result.manual ? (
              <span className="rounded-full border px-2 py-px text-[11px] text-ink-muted">
                manual
              </span>
            ) : null}
            <StatusPill status={result.status} />
          </div>

          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
            <a
              href={result.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 hover:text-ink-secondary hover:underline"
            >
              {result.host}
              <ExternalLink size={11} aria-hidden />
            </a>
            <span aria-hidden>·</span>
            <span className="tabular">
              {result.http_status === null ? 'no response' : `HTTP ${result.http_status}`}
            </span>
            <span aria-hidden>·</span>
            <span>checked {formatRelative(result.checked_at)}</span>
            {result.resolved_url ? (
              <Tooltip
                content={`The configured path redirected on the same host; the probe read ${result.resolved_url}.`}
              >
                <span className="cursor-help underline decoration-dotted">redirected</span>
              </Tooltip>
            ) : null}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <p
              className="tabular text-lg leading-none font-semibold"
              style={{ color: band === 'fast' || band === null ? 'var(--color-ink)' : latencyFill(band) }}
            >
              {formatMs(result.elapsed_ms)}
            </p>
            <p className="text-[11px] text-ink-muted">round trip</p>
          </div>

          <a
            href={detailHref}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={`Open ${result.site_name} ${result.env_label} in a new tab`}
            className="rounded-md border p-1.5 text-ink-secondary transition-colors hover:bg-surface-sunken"
          >
            <SquareArrowOutUpRight size={13} aria-hidden />
          </a>

          <button
            type="button"
            onClick={() => onProbe(result)}
            disabled={isProbing}
            aria-label={`Probe ${result.site_name} ${result.env_label} now`}
            className="rounded-md border p-1.5 text-ink-secondary transition-colors hover:bg-surface-sunken disabled:opacity-60"
          >
            <RefreshCw size={13} aria-hidden className={isProbing ? 'animate-spin' : undefined} />
          </button>

          {onToggleEdit ? (
            <button
              type="button"
              onClick={onToggleEdit}
              aria-label={`Edit the manual monitor for ${result.host}`}
              aria-pressed={isEditing}
              className={cn(
                'rounded-md border p-1.5 transition-colors hover:bg-surface-sunken',
                isEditing ? 'text-ink' : 'text-ink-secondary',
              )}
            >
              <Pencil size={13} aria-hidden />
            </button>
          ) : null}

          {onDelete ? (
            confirmDelete ? (
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onDelete(result)}
                  disabled={isDeleting}
                  className="rounded-md border border-critical/40 bg-critical/10 px-2 py-1.5 text-[11px] font-semibold text-critical transition-colors hover:bg-critical/15 disabled:opacity-60"
                >
                  {isDeleting ? 'Removing…' : 'Remove'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-md border px-2 py-1.5 text-[11px] text-ink-secondary transition-colors hover:bg-surface-sunken"
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                aria-label={`Remove the manual monitor for ${result.host}`}
                className="rounded-md border p-1.5 text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-critical"
              >
                <Trash2 size={13} aria-hidden />
              </button>
            )
          ) : null}
        </div>
      </header>

      {editSlot}

      {controls ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2">
          <ModeToggle
            mode={result.requested_mode}
            onChange={controls.onSetMode}
            detailedDisabledReason={controls.detailedDisabledReason}
            pending={controls.modePending}
          />
          <IntervalControl
            seconds={controls.intervalSeconds}
            isCustom={controls.intervalIsCustom}
            onChange={controls.onSetInterval}
            allowDefault
            pending={controls.intervalPending}
          />
          {/*
            Offered on every card, not only where detailed mode works: "there is no key
            for this host, and here is why" is exactly what an operator is asking when the
            card is stuck on the public view, so a hidden button would withhold the answer.
          */}
          <Tooltip content="Call this site's verify endpoint with the key already held for this host. Nothing is issued and a rejected key is left in place — the next detailed probe verifies it again and replaces it before it fetches health.">
            <button
              type="button"
              onClick={controls.onCheckKey}
              disabled={controls.checkKeyPending}
              className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-ink-secondary transition-colors hover:bg-surface-sunken disabled:opacity-60"
            >
              <ShieldCheck size={11} aria-hidden />
              {controls.checkKeyPending ? 'Checking…' : 'Check key'}
            </button>
          </Tooltip>
        </div>
      ) : null}

      {controls?.keyCheckError ? (
        <p className="flex items-start gap-2 border-b px-4 py-2.5 text-xs text-warning">
          <AlertTriangle size={13} aria-hidden className="mt-0.5 shrink-0" />
          <span className="break-words">
            Could not check the key: {controls.keyCheckError}
          </span>
        </p>
      ) : controls?.keyCheck ? (
        <KeyCheckNotice check={controls.keyCheck} />
      ) : null}

      {confirmDelete ? (
        <p className="border-b px-4 py-2 text-xs text-ink-secondary">
          Stop watching {result.host} and discard its{' '}
          {history?.sample_count ?? 0} sample
          {(history?.sample_count ?? 0) === 1 ? '' : 's'}? Re-add it any time from
          “Check another host”.
        </p>
      ) : null}

      {/* View / credential notices */}
      {result.key_withheld ? (
        <p className="flex items-start gap-2 border-b bg-warning/8 px-4 py-2.5 text-xs text-ink-secondary">
          <KeyRound size={13} aria-hidden className="mt-0.5 shrink-0 text-warning" />
          <span>{result.key_withheld}</span>
        </p>
      ) : result.view === 'public' ? (
        <p className="flex items-start gap-2 border-b px-4 py-2.5 text-xs text-ink-muted">
          <Lock size={13} aria-hidden className="mt-0.5 shrink-0" />
          <span>
            Public view — up/down and latency only.
            {/*
              An expired key cannot be the explanation any more: the backend verifies
              before it probes and replaces a lapsed key first, and a key the endpoint
              refuses outright is re-issued and re-probed before the result is recorded.
              So a key that was sent, and accepted, and still returned the thin shape is
              a key without the scope for detail - which is a different thing to fix, and
              saying "refused or expired" here would send an operator after the wrong one.
            */}
            {result.key_sent
              ? ' A key was sent and accepted, but the response carried the public shape anyway — it is likely scoped without access to check detail.'
              : ' Set this site’s API key environment variable for check detail and the queue snapshot.'}
          </span>
        </p>
      ) : null}

      {result.error ? (
        <p className="flex items-start gap-2 border-b px-4 py-2.5 text-xs text-critical">
          <AlertTriangle size={13} aria-hidden className="mt-0.5 shrink-0" />
          <span className="break-words">{result.error}</span>
        </p>
      ) : null}

      {issueEntries.length > 0 ? (
        <div className="border-b px-4 py-3">
          <p className="mb-1.5 text-xs font-semibold text-ink-secondary">
            {issueEntries.length} issue{issueEntries.length === 1 ? '' : 's'} reported
          </p>
          <ul className="space-y-1">
            {issueEntries.map(([name, detail]) => (
              <li key={name} className="flex gap-2 text-xs">
                <span className={cn('shrink-0 font-medium', statusText('critical'))}>
                  {name}
                </span>
                <span className="min-w-0 break-words text-ink-secondary">{detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* The window */}
      <div className="grid gap-4 border-b p-4 sm:grid-cols-[13rem_minmax(0,1fr)]">
        <div className="space-y-3">
          <Meter
            label="Answered"
            value={formatPct(history?.available_pct ?? null)}
            ratio={(history?.available_pct ?? 0) / 100}
            fill="var(--color-accent)"
            threshold={0.99}
            thresholdLabel="99%"
          />
          <Meter
            label="Fully healthy"
            value={formatPct(history?.healthy_pct ?? null)}
            ratio={(history?.healthy_pct ?? 0) / 100}
            fill={
              (history?.healthy_pct ?? 100) >= 99
                ? 'var(--color-good)'
                : (history?.healthy_pct ?? 0) >= 90
                  ? 'var(--color-warning)'
                  : 'var(--color-critical)'
            }
          />
          <dl className="flex gap-4 text-[11px]">
            <div>
              <dt className="text-ink-muted">p50</dt>
              <dd className="tabular font-semibold text-ink">
                {formatMs(history?.latency_p50_ms ?? null)}
              </dd>
            </div>
            <div>
              <dt className="text-ink-muted">p95</dt>
              <dd className="tabular font-semibold text-ink">
                {formatMs(history?.latency_p95_ms ?? null)}
              </dd>
            </div>
            <div>
              <Tooltip content="Samples are kept by count, not by time — the span depends on the poll interval.">
                <dt className="cursor-help text-ink-muted underline decoration-dotted">
                  samples
                </dt>
              </Tooltip>
              <dd className="tabular font-semibold text-ink">{history?.sample_count ?? 0}</dd>
            </div>
          </dl>
        </div>

        <StatusTimeline
          samplesInput={samples}
          label={`${result.site_name} · ${result.env_label} · ${result.host}`}
          caption={
            samples.length > 0
              ? `${samples.length} samples · oldest ${formatRelative(samples[0]?.t)}`
              : undefined
          }
        />
      </div>

      <ServiceList result={result} samples={samples} thresholds={thresholds} site={site} />

      {queueSnapshot ? (
        <div className="border-t p-4">
          <QueuePanel snapshot={queueSnapshot} />
        </div>
      ) : null}

      {result.raw ? (
        <div className="border-t">
          <button
            type="button"
            onClick={() => setShowRaw((value) => !value)}
            aria-expanded={showRaw}
            className="w-full px-4 py-2.5 text-left text-xs text-ink-secondary hover:bg-surface-sunken"
          >
            {showRaw ? 'Hide' : 'Show'} raw response
          </button>
          {showRaw ? <RawPayload payload={result.raw} /> : null}
        </div>
      ) : null}
    </article>
  )
}

/**
 * The verdict from "Check key", as one line under the controls.
 *
 * `aria-live` so a screen reader hears the answer to a button press it did not visibly
 * change anything else for. The timestamp matters here in a way it does not on most
 * notices: this row persists after the check, and "valid" from four minutes ago is a
 * different claim from "valid" just now.
 */
function KeyCheckNotice({ check }: { check: ApiKeyCheck }) {
  const { icon: Icon, tone, label } = KEY_CHECK_PRESENTATION[check.state]

  return (
    <p
      aria-live="polite"
      className={cn(
        'flex items-start gap-2 border-b px-4 py-2.5 text-xs text-ink-secondary',
        check.state === 'invalid' && 'bg-critical/8',
        check.state === 'valid' && 'bg-good/8',
      )}
    >
      <Icon size={13} aria-hidden className={cn('mt-0.5 shrink-0', tone)} />
      <span className="break-words">
        <span className={cn('font-semibold', tone)}>{label}</span>
        {check.source ? (
          <span className="text-ink-muted"> · {check.source} key</span>
        ) : null}
        <span className="text-ink-muted"> · checked {formatRelative(check.checked_at)}</span>
        <span className="mt-0.5 block">{check.detail}</span>
      </span>
    </p>
  )
}
